import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem('teaching_app_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Track request duration for slow-call warnings in development.
    (config as any).metadata = { startTime: new Date() };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log slow requests in development
    if (import.meta.env.DEV && (response.config as any).metadata) {
      const duration = new Date().getTime() - (response.config as any).metadata.startTime.getTime();
      if (duration > 2000) {
        console.warn(`Slow API request: ${response.config.method?.toUpperCase()} ${response.config.url} took ${duration}ms`);
      }
    }

    return response;
  },
  async (error) => {
    const { response, config } = error;

    const isAuthCheck = config?.url?.includes('/auth/me');
    const isAuthAttempt = config?.url?.includes('/auth/login') || config?.url?.includes('/auth/register');
    const shouldRedirectToLogin = !isAuthCheck && !isAuthAttempt;
    
    if (response) {
      const { status, data } = response;
      
      switch (status) {
        case 401:
          if (shouldRedirectToLogin) {
            console.error('Session expired. Please log in again.');

            localStorage.removeItem('teaching_app_token');
            localStorage.removeItem('teaching_app_user');

            if (!window.location.pathname.includes('/login')) {
              window.location.href = '/login';
            }
          }
          break;
          
        case 403:
          if (!isAuthCheck) {
            console.error(data?.error || 'Access denied');
          }
          break;
          
        case 404:
          if (!isAuthCheck && !config?.skipErrorToast) {
            console.error(data?.error || 'Resource not found');
          }
          break;
          
        case 409:
          if (!isAuthCheck) {
            console.error(data?.error || 'Conflict with existing data');
          }
          break;
          
        case 422:
          if (!isAuthCheck) {
            const message = data?.error || 'Validation failed';
            console.error(message);
          }
          break;
          
        case 429:
          console.error('Too many requests. Please wait a moment and try again.');
          break;
          
        case 500:
        case 502:
        case 503:
        case 504:
          if (!isAuthCheck) {
            console.error('Server error. Please try again later.');
          }
          break;
          
        default:
          if (!isAuthCheck && !config?.skipErrorToast) {
            console.error(data?.error || 'An unexpected error occurred');
          }
      }
    } else if (error.code === 'ECONNABORTED') {
      console.error('Request timed out. Please check your connection.');
    } else if (error.message === 'Network Error') {
      console.error('Network error. Please check your connection.');
    } else {
      if (!isAuthCheck) {
        console.error('An unexpected error occurred');
      }
    }

    return Promise.reject(error);
  }
);

// Helper functions for common request patterns
export const apiClient = {
  // GET request
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return api.get(url, config).then(response => response.data);
  },

  // POST request
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return api.post(url, data, config).then(response => response.data);
  },

  // PUT request
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return api.put(url, data, config).then(response => response.data);
  },

  // PATCH request
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return api.patch(url, data, config).then(response => response.data);
  },

  // DELETE request
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return api.delete(url, config).then(response => response.data);
  },

  // Request with custom error handling
  request: <T = any>(config: AxiosRequestConfig & { skipErrorToast?: boolean }): Promise<T> => {
    return api.request(config).then(response => response.data);
  },
};

// Upload helper for file uploads
export const uploadFile = async (
  url: string,
  file: File,
  onUploadProgress?: (progressEvent: any) => void
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(onUploadProgress && { onUploadProgress }),
  }).then(response => response.data);
};

// Download helper
export const downloadFile = async (url: string, filename?: string): Promise<void> => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    });

    // Create blob link to download
    const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = urlBlob;
    
    // Get filename from response headers or use provided filename
    const disposition = response.headers['content-disposition'];
    let extractedFilename = filename;
    
    if (disposition && !filename) {
      const filenameMatch = disposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        extractedFilename = filenameMatch[1];
      }
    }
    
    link.download = extractedFilename || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(urlBlob);
  } catch (error) {
    console.error('Download failed:', error);
    console.error('Download failed');
    throw error;
  }
};

// Health check
export const healthCheck = async (): Promise<{ status: string; timestamp: string }> => {
  return apiClient.get('/health', { skipErrorToast: true } as any);
};

// Request cancellation helper
export const createCancelToken = () => {
  return axios.CancelToken.source();
};

// Check if error is cancellation
export const isCancel = (error: any): boolean => {
  return axios.isCancel(error);
};

export default api;
